import { useTranslation } from 'react-i18next';
import type { ProfileCompleteness } from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/utils/cn';

interface CustomerProfileCompletenessProps {
  completeness: ProfileCompleteness | undefined;
  isLoading?: boolean;
  onComplete?: () => void;
  canEdit?: boolean;
}

/**
 * Progress toward a fully enriched profile.
 *
 * Informational only — nothing is blocked because a date of birth is missing. The point is to
 * make the gap visible at the moment someone is already looking at the customer.
 */
export function CustomerProfileCompleteness({
  completeness,
  isLoading = false,
  onComplete,
  canEdit = false,
}: CustomerProfileCompletenessProps) {
  const { t } = useTranslation();

  if (isLoading || !completeness) {
    return (
      <div className="space-y-3 p-5">
        <Skeleton className="h-2 w-full" />
        <Skeleton className="h-4 w-32" />
      </div>
    );
  }

  const { percentage, missingFields } = completeness;
  const tone =
    percentage >= 90 ? 'bg-emerald-500' : percentage >= 60 ? 'bg-brand-500' : 'bg-amber-500';

  return (
    <div className="space-y-3 p-5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-ink text-2xl font-semibold" dir="ltr">
          {percentage}%
        </span>
        <span className="text-muted text-xs">{t('customers.completeness.complete')}</span>
      </div>

      <div
        className="bg-line h-2 w-full overflow-hidden rounded-full"
        role="progressbar"
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('customers.completeness.title')}
      >
        {/* Width is a data value, not a style choice, so it stays inline. */}
        <div
          className={cn('h-full rounded-full transition-all', tone)}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {missingFields.length > 0 ? (
        <div>
          <p className="text-muted text-xs font-medium">{t('customers.completeness.missing')}</p>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {missingFields.map((field) => (
              <li
                key={field}
                className="text-muted bg-canvas border-line rounded border px-2 py-0.5 text-xs"
              >
                {t(`customers.fields.${field}`)}
              </li>
            ))}
          </ul>

          {canEdit && onComplete ? (
            <Button variant="secondary" size="sm" className="mt-3" onClick={onComplete}>
              {t('customers.completeness.action')}
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-emerald-700">{t('customers.completeness.allDone')}</p>
      )}
    </div>
  );
}
